// =========================================================================
// User-facing SMS auth + opt-in + 2FA + preferences.
//
// Two surfaces here:
//
//   1. Direct `core.rpc_user_*` RPCs (PostgREST). Used for status reads
//      and the prefs toggle — anything that doesn't need to dispatch SMS.
//
//   2. Edge function `user-sms-auth` (POST /functions/v1/user-sms-auth).
//      Used for every flow that needs to actually SEND an SMS (OTP start,
//      MFA login challenge, etc.). The function calls the same RPCs under
//      the caller's JWT, then dispatches via service-role.
//
// All read-RPCs are typed via the generated `Database` shape. The edge-
// function actions are typed manually because the function body is the
// source of truth, not Postgres.
// =========================================================================

import { supabase } from '../client';
import { unwrapFunctionsInvokeError } from '../internal/functionsInvokeError';

// -------------------------------------------------------------------------
// Direct RPCs (no SMS dispatch)
// -------------------------------------------------------------------------

export interface SmsOptinLink {
  platform_number: string;
  account_id: string;
  display_name: string;
  prewritten_body: string;
  /** sms:NUMBER?&body=URLENCODED -- works on iOS Messages + Android Messages. */
  sms_url: string;
}

/** Get the components needed to render the "Tap to text us" SMS link. */
export async function getSmsOptinLink(): Promise<SmsOptinLink> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_user_get_sms_optin_link');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as unknown as SmsOptinLink;
}

export interface SmsOptinStatus {
  has_phone: boolean;
  phone: string | null;
  phone_verified: boolean;
  opted_in: boolean;
  opted_in_at: string | null;
  opted_out_at: string | null;
}

/** Check whether the current user's phone is opted in to the platform SMS line. */
export async function getSmsOptinStatus(): Promise<SmsOptinStatus> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_user_sms_optin_status');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as unknown as SmsOptinStatus;
}

export type SmsPrefField =
  | 'notify_on_login'
  | 'notify_on_password_reset'
  | 'notify_on_email_change'
  | 'notify_on_security_event'
  | 'notify_on_new_invitation'
  | 'notify_on_phone_change'
  | 'marketing_optin';

export interface UserSmsPreferences {
  user_id: string;
  notify_on_login: boolean;
  notify_on_password_reset: boolean;
  notify_on_email_change: boolean;
  notify_on_security_event: boolean;
  notify_on_new_invitation: boolean;
  notify_on_phone_change: boolean;
  mfa_enabled: boolean;
  mfa_factor: 'sms' | 'email';
  mfa_enrolled_at: string | null;
  mfa_disabled_at: string | null;
  marketing_optin: boolean;
  created_at: string;
  updated_at: string;
}

/** Read the current user's SMS preference row (lazily creates if missing). */
export async function getMySmsPreferences(): Promise<UserSmsPreferences | null> {
  const { data: sessionData } = await supabase().auth.getUser();
  const uid = sessionData?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase()
    .schema('core')
    .from('user_sms_preferences')
    .select('*')
    .eq('user_id', uid)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as UserSmsPreferences | null) ?? null;
}

/** Toggle a single SMS preference flag. */
export async function setSmsPref(
  field: SmsPrefField,
  value: boolean,
): Promise<UserSmsPreferences> {
  const result = await callUserSmsAuth({
    action: 'set_pref',
    field,
    value,
  });
  return result.preferences as UserSmsPreferences;
}

// -------------------------------------------------------------------------
// Edge function: user-sms-auth
// -------------------------------------------------------------------------

interface UserSmsAuthOkBase {
  ok: true;
  challenge_id?: string;
  expires_at?: string;
  verified_at?: string;
  phone?: string;
  next_challenge_id?: string;
  new_phone?: string;
  recovery_codes?: string[];
  remaining_codes?: number;
  preferences?: UserSmsPreferences;
}

interface UserSmsAuthError {
  ok: false;
  error: string;
}

type UserSmsAuthResponse = UserSmsAuthOkBase | UserSmsAuthError;

interface UserSmsAuthRequest {
  action:
    | 'phone_start'
    | 'phone_confirm'
    | 'phone_change_start'
    | 'phone_change_confirm_current'
    | 'mfa_setup_start'
    | 'mfa_setup_confirm'
    | 'mfa_email_setup_start'
    | 'mfa_email_setup_confirm'
    | 'mfa_enroll_start'
    | 'mfa_enroll_confirm'
    | 'mfa_disable_start'
    | 'mfa_disable_confirm'
    | 'mfa_login_start'
    | 'mfa_login_verify'
    | 'mfa_email_login_start'
    | 'mfa_email_login_verify'
    | 'recovery_use'
    | 'recovery_regenerate'
    | 'set_pref';
  phone?: string;
  new_phone?: string;
  code?: string;
  auth_session_id?: string;
  field?: SmsPrefField;
  value?: boolean;
}

/**
 * Generic invoker for the user-sms-auth edge function. Throws on `ok: false`
 * with the server's error message; returns the success payload on `ok: true`.
 */
async function callUserSmsAuth(body: UserSmsAuthRequest): Promise<UserSmsAuthOkBase> {
  const { data, error } = await supabase().functions.invoke<UserSmsAuthResponse>(
    'user-sms-auth',
    { body: body as unknown as Record<string, unknown> },
  );
  if (error) {
    throw new Error(await unwrapFunctionsInvokeError(error, 'user-sms-auth call failed'));
  }
  if (!data) throw new Error('user-sms-auth returned no body');
  if (!data.ok) throw new Error(data.error);
  return data;
}

// ---- phone verify (initial) ---------------------------------------------

export interface PhoneStartResult {
  challenge_id?: string;
  expires_at?: string;
  /** When true, the phone hasn't opted in yet -- frontend must switch to
   *  the opt-in flow (text us first) instead of expecting an OTP. */
  requires_optin?: boolean;
  platform_number?: string;
  account_id?: string;
  sms_url?: string;
}

export async function startPhoneVerify(phone: string): Promise<PhoneStartResult> {
  // This action is special: rpc_user_phone_start_verify may return EITHER
  // a challenge_id (OTP sent) OR requires_optin=true with the opt-in link.
  // We call the RPC directly so we can pick up the full row shape, instead
  // of bouncing through the edge function (which only forwards challenge_id).
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_user_phone_start_verify', { p_phone: phone });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  if (!row) throw new Error('No response from rpc_user_phone_start_verify');
  return {
    challenge_id: (row.challenge_id as string | null) ?? undefined,
    expires_at: (row.expires_at as string | null) ?? undefined,
    requires_optin: Boolean(row.requires_optin),
    platform_number: (row.platform_number as string | null) ?? undefined,
    account_id: (row.account_id as string | null) ?? undefined,
    sms_url: (row.sms_url as string | null) ?? undefined,
  };
}

export async function confirmPhoneVerify(code: string): Promise<{ verified_at: string; phone: string }> {
  const r = await callUserSmsAuth({ action: 'phone_confirm', code });
  return { verified_at: r.verified_at!, phone: r.phone! };
}

// ---- phone change (gated on current-phone OTP) --------------------------

export async function startPhoneChange(newPhone: string): Promise<{ challenge_id: string; expires_at: string }> {
  const r = await callUserSmsAuth({ action: 'phone_change_start', new_phone: newPhone });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function confirmPhoneChangeCurrent(code: string): Promise<{
  next_challenge_id: string;
  expires_at: string;
  new_phone: string;
}> {
  const r = await callUserSmsAuth({ action: 'phone_change_confirm_current', code });
  return {
    next_challenge_id: r.next_challenge_id!,
    expires_at: r.expires_at!,
    new_phone: r.new_phone!,
  };
}

// ---- 2FA self-serve setup (one-shot phone-verify + enroll) --------------
//
// Replaces the old two-trip flow (text us first -> startMfaEnroll). The
// user picks a phone in the modal, we text a single OTP to that number,
// and `confirmMfaSetup` both verifies the phone AND flips mfa_enabled +
// returns the 10 recovery codes in a single round trip.
//
// `startMfaEnroll` / `confirmMfaEnroll` below are retained for admin
// tooling and the historical recovery path that assumes a pre-verified
// phone on file; new UI should call the `MfaSetup` pair.

export async function startMfaSetup(phone: string): Promise<{ challenge_id: string; expires_at: string }> {
  const r = await callUserSmsAuth({ action: 'mfa_setup_start', phone });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function confirmMfaSetup(code: string): Promise<{ recovery_codes: string[] }> {
  const r = await callUserSmsAuth({ action: 'mfa_setup_confirm', code });
  return { recovery_codes: r.recovery_codes ?? [] };
}

export async function startMfaEmailSetup(): Promise<{ challenge_id: string; expires_at: string }> {
  const r = await callUserSmsAuth({ action: 'mfa_email_setup_start' });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function confirmMfaEmailSetup(code: string): Promise<{ recovery_codes: string[] }> {
  const r = await callUserSmsAuth({ action: 'mfa_email_setup_confirm', code });
  return { recovery_codes: r.recovery_codes ?? [] };
}

// ---- 2FA enroll (legacy: assumes a pre-verified phone) / disable --------

export async function startMfaEnroll(): Promise<{ challenge_id: string; expires_at: string }> {
  const r = await callUserSmsAuth({ action: 'mfa_enroll_start' });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function confirmMfaEnroll(code: string): Promise<{ recovery_codes: string[] }> {
  const r = await callUserSmsAuth({ action: 'mfa_enroll_confirm', code });
  return { recovery_codes: r.recovery_codes ?? [] };
}

export async function startMfaDisable(): Promise<{ challenge_id: string; expires_at: string }> {
  const r = await callUserSmsAuth({ action: 'mfa_disable_start' });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function confirmMfaDisable(code: string): Promise<void> {
  await callUserSmsAuth({ action: 'mfa_disable_confirm', code });
}

// ---- 2FA at login -------------------------------------------------------

export async function startMfaLogin(args?: {
  factor?: 'sms' | 'email';
}): Promise<{ challenge_id: string; expires_at: string }> {
  const action = args?.factor === 'email' ? 'mfa_email_login_start' : 'mfa_login_start';
  const r = await callUserSmsAuth({ action });
  return { challenge_id: r.challenge_id!, expires_at: r.expires_at! };
}

export async function verifyMfaLogin(args: {
  code: string;
  authSessionId: string;
  factor?: 'sms' | 'email';
}): Promise<{ verified_at: string }> {
  const action = args.factor === 'email' ? 'mfa_email_login_verify' : 'mfa_login_verify';
  const r = await callUserSmsAuth({
    action,
    code: args.code,
    auth_session_id: args.authSessionId,
  });
  return { verified_at: r.verified_at! };
}

// NOTE: intentionally NOT named `useRecoveryCode` -- starting with "use"
// makes ESLint's react-hooks/rules-of-hooks treat any call site as a Hook
// invocation, which breaks compilation in any non-component caller.
export async function redeemRecoveryCode(args: {
  code: string;
  authSessionId: string;
}): Promise<{ verified_at: string; remaining_codes: number }> {
  const r = await callUserSmsAuth({
    action: 'recovery_use',
    code: args.code,
    auth_session_id: args.authSessionId,
  });
  return { verified_at: r.verified_at!, remaining_codes: r.remaining_codes ?? 0 };
}

// ---- regenerate recovery codes (two-step: OTP gate) ---------------------

export interface RegenStartResult {
  kind: 'challenge_started';
  challenge_id: string;
  expires_at: string;
}
export interface RegenDoneResult {
  kind: 'codes_issued';
  recovery_codes: string[];
}

export async function regenerateRecoveryCodes(code?: string): Promise<RegenStartResult | RegenDoneResult> {
  const r = await callUserSmsAuth({ action: 'recovery_regenerate', code });
  if (r.recovery_codes && r.recovery_codes.length > 0) {
    return { kind: 'codes_issued', recovery_codes: r.recovery_codes };
  }
  return {
    kind: 'challenge_started',
    challenge_id: r.challenge_id!,
    expires_at: r.expires_at!,
  };
}

// -------------------------------------------------------------------------
// MFA claim helpers (read from the access-token JWT)
// -------------------------------------------------------------------------

/** Extracted MFA state from the user's JWT app_metadata claims. */
export interface MfaClaims {
  required: boolean;
  verified_at: string | null;
  /** session_id from the JWT, needed when calling mfa_login_verify. */
  auth_session_id: string | null;
}

/**
 * Reads MFA state for the current session.
 *
 * Two-stage resolution:
 *
 *   1. **Fast path (JWT)**: decode `app_metadata.mfa_required` /
 *      `mfa_verified_at` from the live access token. These claims are
 *      injected by `auth-audit-hook` (Custom Access Token Hook); when
 *      the hook is registered AND the user enrolled in 2FA before the
 *      current token was minted, this returns the answer with zero
 *      network round-trips.
 *
 *   2. **Fallback path (RPC)**: if the JWT carries the `mfa_required`
 *      claim falsy, we re-check by calling
 *      `core.rpc_user_mfa_status(auth_session_id)`. This catches:
 *
 *        - The Custom Access Token Hook is not registered in the
 *          Supabase Auth dashboard (silent config drift; the
 *          access_token is issued without our app_metadata claims).
 *        - The user just enrolled in 2FA but the current token
 *          predates the enrollment and hasn't refreshed yet.
 *
 *      The RPC is `stable security definer` and reads the caller's own
 *      `core.user_sms_preferences` + `core.user_mfa_sessions` rows, so
 *      its answer is always server-authoritative.
 *
 * The combined effect is: 2FA always enforces, every SPA, regardless of
 * whether the hook is wired in the dashboard.
 *
 * Returns null when there's no session.
 */
export async function readMfaClaims(): Promise<MfaClaims | null> {
  const { data } = await supabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;

  let jwtRequired = false;
  let jwtVerifiedAt: string | null = null;
  let sessionId: string | null = null;

  try {
    const payload = decodeJwtPayload(token) as Record<string, unknown> & {
      app_metadata?: { mfa_required?: boolean; mfa_verified_at?: string };
      session_id?: string;
    };
    const meta = payload.app_metadata ?? {};
    jwtRequired = Boolean(meta.mfa_required);
    jwtVerifiedAt =
      typeof meta.mfa_verified_at === 'string' ? meta.mfa_verified_at : null;
    sessionId = typeof payload.session_id === 'string' ? payload.session_id : null;
  } catch {
    // JWT malformed -- fall through to the RPC path which will treat
    // the session as anonymous and return required=false.
  }

  // If JWT says MFA required AND verified_at is present, fast-path allow.
  // When required=true but verified_at is missing, we still consult the
  // server so a stale hook claim doesn't trap users on /mfa after success.
  if (jwtRequired && jwtVerifiedAt) {
    return {
      required: true,
      verified_at: jwtVerifiedAt,
      auth_session_id: sessionId,
    };
  }

  // JWT says MFA not required OR says required but unverified. Confirm with
  // the server so we don't get bypassed by stale claims, and don't get stuck
  // on challenge after a successful verify when hook claims lag.
  //
  // The cast through `as any` is intentional: the new RPC
  // `core.rpc_user_mfa_status` lives in migration 20260606020000_* and
  // hasn't been picked up by the generated `Database` types yet. The
  // shape is stable (the migration's `returns table (...)` block is the
  // source of truth) and the response is defensively narrowed below.
  try {
    const { data: rows } = await (
      supabase().schema('core') as unknown as {
        rpc: (
          name: string,
          args: Record<string, unknown>,
        ) => Promise<{ data: unknown; error: unknown }>;
      }
    ).rpc('rpc_user_mfa_status', { p_auth_session_id: sessionId });
    const row = Array.isArray(rows) ? rows[0] : rows;
    if (row && typeof row === 'object') {
      const r = row as {
        mfa_required?: boolean;
        mfa_verified_at?: string | null;
        auth_session_id?: string | null;
      };
      return {
        required: Boolean(r.mfa_required),
        verified_at: typeof r.mfa_verified_at === 'string' ? r.mfa_verified_at : null,
        auth_session_id:
          (typeof r.auth_session_id === 'string' && r.auth_session_id) || sessionId,
      };
    }
  } catch {
    // RPC failure (network / missing migration / RLS misconfig). Be
    // permissive so a temporary RPC outage doesn't lock everyone out;
    // the gate already enforces "have a session" upstream, and any
    // sensitive sensitive_action paths re-check MFA themselves.
  }

  return {
    required: false,
    verified_at: jwtVerifiedAt,
    auth_session_id: sessionId,
  };
}

/**
 * After verify, force the access token to refresh so the next claims read
 * picks up the new `mfa_verified_at`. supabase-js exposes `refreshSession`
 * for this -- it's a no-op if no refresh is needed.
 */
export async function refreshAccessToken(): Promise<void> {
  await supabase().auth.refreshSession();
}

// -------------------------------------------------------------------------
// Trusted devices ("remember this device for 30 days" -- OTP skip only)
//
// These RPCs live in migration 20260703080000_user_mfa_trusted_devices.sql
// and aren't in the generated `Database` types yet, so we go through the
// same `schema('core') as unknown as { rpc }` cast pattern `readMfaClaims`
// uses for `rpc_user_mfa_status`.
// -------------------------------------------------------------------------

function coreRpc() {
  return supabase().schema('core') as unknown as {
    rpc: (
      name: string,
      args?: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: unknown }>;
  };
}

/**
 * Try to skip the MFA OTP step using a previously-trusted device token.
 * Returns the `verified_at` timestamp when the token matched a live
 * trusted-device row (the server minted a `user_mfa_sessions` row for the
 * current JWT session); returns null when there is nothing to redeem and
 * the caller should fall through to the normal OTP challenge.
 */
export async function redeemTrustedDevice(args: {
  deviceToken: string;
  authSessionId: string;
}): Promise<{ verified_at: string } | null> {
  const { data, error } = await coreRpc().rpc('rpc_user_mfa_redeem_trusted_device', {
    p_device_token: args.deviceToken,
    p_auth_session_id: args.authSessionId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  const verifiedAt = (row as { verified_at?: string } | null)?.verified_at;
  return typeof verifiedAt === 'string' ? { verified_at: verifiedAt } : null;
}

/**
 * Remember the current browser as a trusted device (30-day OTP skip).
 * Requires the current session to already be MFA-verified server-side.
 */
export async function trustDevice(args: {
  deviceToken: string;
  label: string;
  authSessionId: string;
}): Promise<{ expires_at: string }> {
  const { data, error } = await coreRpc().rpc('rpc_user_mfa_trust_device', {
    p_device_token: args.deviceToken,
    p_label: args.label,
    p_auth_session_id: args.authSessionId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  const expiresAt = (row as { expires_at?: string } | null)?.expires_at;
  return { expires_at: typeof expiresAt === 'string' ? expiresAt : '' };
}

/** Revoke every trusted device for the current user. Returns the count revoked. */
export async function forgetTrustedDevices(): Promise<number> {
  const { data, error } = await coreRpc().rpc('rpc_user_mfa_forget_trusted_devices');
  if (error) throw error;
  const n = Array.isArray(data) ? data[0] : data;
  return typeof n === 'number' ? n : 0;
}

/** Count of the current user's active (unrevoked, unexpired) trusted devices. */
export async function getTrustedDeviceCount(): Promise<number> {
  const { data, error } = await coreRpc().rpc('rpc_user_mfa_trusted_device_count');
  if (error) throw error;
  const n = Array.isArray(data) ? data[0] : data;
  return typeof n === 'number' ? n : 0;
}

function decodeJwtPayload(token: string): unknown {
  const parts = token.split('.');
  const payloadPart = parts[1];
  if (parts.length !== 3 || !payloadPart) throw new Error('Malformed JWT');
  // base64url -> base64 -> bytes
  const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  const padded = pad ? b64 + '='.repeat(4 - pad) : b64;
  const json = typeof atob === 'function'
    ? atob(padded)
    : Buffer.from(padded, 'base64').toString('utf-8');
  return JSON.parse(json);
}
